"use client";

import { OnboardingItemLayout } from "@/components/Onboarding/components/OnboardingItemLayout";
import { Button } from "@/components/ui/button";
import {
  Action,
  ControlType,
  useOnboardingAnalytics,
} from "@/context/AnalyticsProvider";
import { useOnboardingStep } from "@/context/OnboardingStepProvider/OnboardingStepProvider";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef } from "react";
import { OnboardingStep, type OnboardingStepProps } from "../constants";

export const SeeProgressInfo = ({ visible }: OnboardingStepProps) => {
  const { track } = useOnboardingAnalytics();
  const { currentStep, goToNextStep } = useOnboardingStep();
  const hasViewedRef = useRef(false);

  useEffect(() => {
    if (hasViewedRef.current || !visible) return;
    hasViewedRef.current = true;

    track({
      action: Action.VIEWED,
      extras: {
        id: OnboardingStep.SEE_PROGRESS_INFO,
        step: currentStep,
      },
    });
  }, [track, visible, currentStep]);

  const handleButtonClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.SEE_PROGRESS_INFO,
        step: currentStep,
        control: "next-step",
        controlType: ControlType.BUTTON,
      },
    });

    goToNextStep();
  };

  const handleImageClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.SEE_PROGRESS_INFO,
        step: currentStep,
        control: "see-progress",
        controlType: ControlType.IMAGE,
      },
    });
  };

  return (
    <OnboardingItemLayout>
      <div className="text-center">
        <h1 className="imx-gradient font-bold text-sm tracking-wider">
          HOW IT WORKS
        </h1>

        <h2 className="font-bold text-3xl">See your progress</h2>

        <p className="my-2">
          See your draw projection improve as you
          <br />
          complete quests on the rewards progress bar.
        </p>

        <Image
          src="/images/onboarding/see-progress-info.webp"
          alt="See your progress"
          width={760}
          height={397}
          className="w-full max-h-[35dvh] object-contain"
          onClick={handleImageClick}
          priority
        />
      </div>

      <p className="text-center">
        Remember: Find out what you've won each Thursday at{" "}
        <Link
          href="https://play.immutable.com/rewards"
          target="_blank"
          rel="noreferrer"
          className="text-white font-bold underline"
        >
          play.immutable.com/rewards.
        </Link>
      </p>

      <Button
        size="lg"
        className="w-full rounded-full py-4 text-base font-semibold relative"
        onClick={handleButtonClick}
      >
        Next step
        <ArrowRight className="absolute size-5 right-4" />
      </Button>
    </OnboardingItemLayout>
  );
};
